import { toast } from "@carbon6/c6-frontend-design";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  cancelTrainingSchedule,
  listOperationsSchedules,
  pauseTrainingSchedule,
  resumeTrainingSchedule,
  type TrainingScheduleOperationsItem,
  type TrainingScheduleView,
} from "@/api/trainingSchedule";
import type { UserRole } from "@/app";
import { resolveLoadErrorMessage, type LoadState } from "@/components/dataset/loadState";
import { createLogger } from "@/logging";
import { subscribeScheduleChanged } from "./scheduleBus";

const logger = createLogger("page/training/schedules");

export type TrainingScheduleCommand = "pause" | "resume" | "cancel";

// 자동 새로고침 주기. ticker 가 1분마다 실행되어 next_run_at 과 실행 중 Session 수가 그 주기로
// 바뀐다. 더 짧으면 왕복만 늘고, 더 길면 방금 실행된 항목이 한참 뒤에 보인다. 주기는 이전 응답부터 잰다.
export const SCHEDULE_LIST_POLL_INTERVAL_MS = 60_000;

// 조회 한 번의 성격. `silent` 는 요청마다 다르다. 자동 새로고침의 실패는 로그만 남기고,
// 사용자가 호출한 재적재의 실패는 toast 로 알린다.
type LoadOptions = { silent: boolean };

export type TrainingSchedulesController = {
  state: LoadState<readonly TrainingScheduleOperationsItem[]>;
  // 지금 명령이 진행 중인 스케줄 id. 그 행의 버튼만 잠근다(목록 전체를 잠그면 다른 행을
  // 조작할 수 없다).
  pendingId: string | null;
  // 목록을 표시한 채 다시 받는 중. 새로고침 버튼만 잠근다.
  refreshing: boolean;
  reload: () => void;
  // 일시정지와 재개는 갱신된 스케줄 view 를 반환한다(열린 다이얼로그가 상태를 교체한다).
  // 삭제는 soft-cancel 이라 반환할 행이 없어 null 이다.
  run: (
    command: TrainingScheduleCommand,
    scheduleId: string,
  ) => Promise<TrainingScheduleView | null>;
};

/**
 * Operations > Schedule 목록(전역)과 그 위의 명령(일시정지·재개·삭제).
 *
 * 목록은 operations 라우트 1왕복이다. 스케줄 view, recipe 요약, 실행 중 세션 수가 한 응답에
 * 들어온다. 그래서 화면이 행마다 다시 묻지 않는다(rules §7). 전체 응답이라 `DataTableSource`
 * 페이징을 쓰지 않는다. 페이지 축이 없는 목록에 페이징 source 를 쓰면 없는 개념을 만든다.
 *
 * `enabled` 가 false 면(자격 없음. 게이트가 본문 대신 안내를 그린다) 조회하지 않는다. 자격 없이
 * 호출하면 서버가 403 을 반환한다. 보이지 않는 화면을 위해 실패할 요청을 보내는 것은 불필요하다.
 *
 * 조회 수명주기는 effect 하나가 모두 소유한다(rules §12: 단일 effect 안에서 끝나는 리소스는 별도
 * 수명주기 객체를 만들지 않는다). 그 effect 가 지키는 것은 세 가지다:
 *
 * - **잠금**: 이전 응답이 오기 전에 같은 조회를 다시 보내지 않는다(rules §7). 그 사이 들어온
 *   요청은 버리지 않고 하나로 합쳐 두었다가 응답 뒤에 이어서 받는다.
 * - **타이머**: 다음 주기는 응답을 받은 뒤에 등록한다. 숨긴 탭에서는 타이머를 제거하고, 다시
 *   보이면 한 번 받으면서 다시 등록한다(`PollingController` 와 같은 규칙).
 * - **세대**: 명령이 목록을 바꾸면 세대가 올라가고, 그보다 오래된 조회 응답은 버린다. 이 가드가
 *   없으면 명령 직전에 보낸 조회가 돌아와 방금 바꾼 행을 되돌린다.
 *
 * 일시정지와 재개는 응답에 갱신된 스케줄이 들어오므로 목록의 그 행만 그 자리에서 교체한다.
 * 목록을 다시 받으면 표가 loading 으로 비었다가 다시 나타나서 켜고 끌 때마다 화면이 깜빡인다.
 * 삭제는 반환할 행이 없어 목록을 다시 받는다. 다른 표면의 변경(scheduleBus)도 재적재로 받는다.
 * "새 Schedule" 창의 생성 완료는 scheduleBus 신호로 받아 같은 재적재를 실행한다.
 */
export function useTrainingSchedules(
  role: UserRole | null,
  enabled: boolean,
): TrainingSchedulesController {
  const [state, setState] = useState<LoadState<readonly TrainingScheduleOperationsItem[]>>({
    status: "idle",
  });
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const stateRef = useRef(state);
  stateRef.current = state;
  // 명령이 목록을 바꿀 때마다 올라간다. 조회는 시작 시점의 세대를 보관하고 응답에서 다시 비교한다.
  // 그 사이 세대가 올랐으면 자기 응답을 버린다.
  const generationRef = useRef(0);
  // effect 가 소유한 조회 진입점. 새로고침 버튼, scheduleBus, 명령이 이 ref 로 들어온다.
  // effect 밖에 두면 타이머와 잠금을 effect 와 나눠 가지게 되어 소유자가 둘이 된다.
  const loadRef = useRef<((options: LoadOptions) => void) | null>(null);

  useEffect(() => {
    if (!enabled) {
      setState({ status: "idle" });
      return;
    }
    let cancelled = false;
    let loading = false;
    // 잠금에 막힌 요청 하나를 보관한다. 사용자 요청(silent=false)이 우선한다. 그 요청의 실패는 알려야 한다.
    let queued: LoadOptions | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const isVisible = (): boolean => document.visibilityState !== "hidden";

    const scheduleNext = (): void => {
      clearTimeout(timer);
      // 숨긴 탭에는 타이머를 등록하지 않는다. 돌아올 때 visibilitychange 가 받으면서 다시 등록한다.
      if (cancelled || !isVisible()) {
        return;
      }
      timer = setTimeout(() => {
        // 타이머를 등록한 뒤 탭이 숨겨졌을 수 있다. visibilitychange 없이 숨겨지는 경로가 있으므로
        // 실행 시점에 다시 확인한다. 실행하지 않고 끝내면 돌아올 때 visibilitychange 가 다시 등록한다.
        if (isVisible()) {
          void load({ silent: true });
        }
      }, SCHEDULE_LIST_POLL_INTERVAL_MS);
    };

    const load = async (options: LoadOptions): Promise<void> => {
      if (cancelled) {
        return;
      }
      if (loading) {
        queued = { silent: (queued?.silent ?? true) && options.silent };
        return;
      }
      clearTimeout(timer);
      loading = true;
      const generation = generationRef.current;
      // 이미 표시한 목록이 있으면 표를 비우지 않고 백그라운드에서 받는다. 새로고침마다 표가 사라졌다가
      // 다시 나타나면 사용자가 읽던 위치를 잃는다. 실패하면 보던 목록을 남기고 toast 로만 알린다.
      const inPlace = stateRef.current.status === "success";
      if (inPlace) {
        setRefreshing(true);
      } else {
        setState({ status: "loading" });
      }
      try {
        const view = await listOperationsSchedules();
        // 기다리는 사이 명령이 목록을 바꿨으면 이 응답은 오래된 값이다. 방금 바꾼 행을 되돌리지 않는다.
        if (!cancelled && generation === generationRef.current) {
          setState({ status: "success", data: view.items });
        }
      } catch (error: unknown) {
        if (!cancelled) {
          logger.error("Failed to list operations schedules.", error);
          if (inPlace) {
            if (!options.silent) {
              toast.error(
                `Schedule 목록을 다시 받지 못했습니다. ${resolveLoadErrorMessage(error)}`,
              );
            }
          } else {
            setState({ status: "error", message: resolveLoadErrorMessage(error) });
          }
        }
      } finally {
        loading = false;
        const next = queued;
        queued = null;
        if (!cancelled) {
          if (next) {
            void load(next);
          } else {
            setRefreshing(false);
            scheduleNext();
          }
        }
      }
    };

    loadRef.current = (options) => void load(options);

    const onVisibilityChange = (): void => {
      if (!isVisible()) {
        clearTimeout(timer);
        return;
      }
      void load({ silent: true });
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    void load({ silent: false });
    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      loadRef.current = null;
    };
  }, [enabled]);

  // 자격이 없으면 loadRef 가 비어 있어 아무 일도 하지 않는다. 버스 구독과 폴링이 같은 게이트를 본다.
  const reload = useCallback(() => loadRef.current?.({ silent: false }), []);

  // 다른 표면(새 Schedule 창)의 변경을 이 목록이 놓치지 않도록 버스를 구독한다.
  useEffect(() => subscribeScheduleChanged(reload), [reload]);

  const run = useCallback(
    async (command: TrainingScheduleCommand, scheduleId: string) => {
      setPendingId(scheduleId);
      try {
        if (command === "cancel") {
          await cancelTrainingSchedule(scheduleId, role);
          // 명령 응답이 온 뒤에 올린다. 명령을 기다리는 동안 보낸 조회도 명령 전의 목록을 가져온다.
          // 먼저 올리면 그 조회 응답을 버리지 못한다.
          generationRef.current += 1;
          reload();
          return null;
        }
        const updated =
          command === "pause"
            ? await pauseTrainingSchedule(scheduleId, role)
            : await resumeTrainingSchedule(scheduleId, role);
        generationRef.current += 1;
        setState((prev) =>
          prev.status === "success"
            ? {
                status: "success",
                data: prev.data.map((row) =>
                  row.id === updated.id ? { ...row, ...updated } : row,
                ),
              }
            : prev,
        );
        return updated;
      } finally {
        setPendingId(null);
      }
    },
    [reload, role],
  );

  return { state, pendingId, refreshing, reload, run };
}
